import type {OptOutStatus} from "./unsubscribeApi";

/**
 * State machine for the unsubscribe page, kept out of the component so the rules are testable and the
 * view stays a pure rendering of the current state (no business logic inside React).
 */
export type UnsubscribeState =
	| {kind: "loading"}
	/** Token resolved; waiting for the recipient to confirm. */
	| {kind: "confirming"; email: string}
	/** Token resolved and this address had already opted out on an earlier click. */
	| {kind: "already"; email: string}
	| {kind: "submitting"; email: string}
	| {kind: "done"; email: string}
	/** Token absent, malformed, tampered with, or signed by another key. */
	| {kind: "invalid"};

/** @returns the state to show once the token lookup succeeds. */
export function resolvedState(status: OptOutStatus): UnsubscribeState {
	return status.unsubscribed
		? {kind: "already", email: status.email}
		: {kind: "confirming", email: status.email};
}

/**
 * @returns the state entered when the recipient confirms. Only a resolved, not-yet-submitting state can
 * start a submit; anything else stays put so a double click cannot fire two requests.
 */
export function submittingState(current: UnsubscribeState): UnsubscribeState {
	return current.kind === "confirming" ? {kind: "submitting", email: current.email} : current;
}

/**
 * @returns the terminal success state. Uses the email the api echoed back rather than the one held in
 * component state, so what is shown is what the server actually suppressed.
 */
export function completedState(status: OptOutStatus): UnsubscribeState {
	return {kind: "done", email: status.email};
}

/** @returns whether the confirm control should be offered in this state. */
export function canConfirm(state: UnsubscribeState): boolean {
	return state.kind === "confirming";
}
